import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Button
} from '@mui/material';
import {
    CalendarToday as CalendarTodayIcon,
    Event as EventIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const EmployeeTimeOffSummary = ({ userId, orgId }) => {
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (userId && orgId) {
            console.log("TimeOffSummary - Fetching data with userId:", userId, "orgId:", orgId);
            fetchApprovedTimeOff();
        } else {
            console.warn("TimeOffSummary - Missing userId or orgId:", { userId, orgId });
            setLoading(false);
        }
    }, [userId, orgId]);

    const fetchApprovedTimeOff = async () => {
        if (!userId || !orgId) return;

        setLoading(true);
        setError(null);

        try {
            const requestsRef = collection(db, "organizations", orgId, "timeOffRequests");
            const q = query(
                requestsRef,
                where("userId", "==", userId),
                where("status", "==", "approved"),
                orderBy("startDate", "asc")
            );

            console.log("TimeOffSummary - Executing Firestore query");
            const snapshot = await getDocs(q);
            console.log("TimeOffSummary - Query returned", snapshot.docs.length, "documents");

            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to beginning of day for comparison

            const timeOffRequests = snapshot.docs.map(doc => {
                // Convert Firestore timestamps to JS Date objects
                const data = doc.data();
                const startDate = data.startDate?.toDate?.() || new Date(data.startDate);
                const endDate = data.endDate?.toDate?.() || new Date(data.endDate);

                // Determine if request is current, upcoming, or past
                const isActive = (startDate <= today && endDate >= today);
                const isUpcoming = (startDate > today);
                const isPast = (endDate < today);

                return {
                    id: doc.id,
                    ...data,
                    startDate,
                    endDate,
                    status: isActive ? "active" : isUpcoming ? "upcoming" : "past"
                };
            });

            // Filter out past requests
            const relevantRequests = timeOffRequests.filter(req => req.status !== "past");
            console.log("TimeOffSummary - Relevant requests:", relevantRequests.length);
            setApprovedRequests(relevantRequests);
        } catch (err) {
            console.error("TimeOffSummary - Error fetching approved time-off:", err);
            setError("Failed to load your approved time-off periods.");
        } finally {
            setLoading(false);
        }
    };

    // Format date to display in a user-friendly way
    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Check if there are any active time-off periods
    const hasActiveTimeOff = approvedRequests.some(req => req.status === "active");

    // Custom ListItemText to avoid nesting p elements
    const CustomListItemText = ({ primary, secondary }) => (
        <Box sx={{ width: '100%' }}>
            {primary}
            <Box sx={{ color: 'text.secondary', mt: 0.5 }}>{secondary}</Box>
        </Box>
    );

    return (
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'background.default', border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                    Your Time Off
                </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            ) : approvedRequests.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    You don't have any active or upcoming approved time off.
                </Alert>
            ) : (
                <>
                    {hasActiveTimeOff && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" component="h3" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
                                Currently On Leave
                            </Typography>

                            {approvedRequests
                                .filter(req => req.status === "active")
                                .map(request => (
                                    <Box key={request.id} sx={{ mb: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                                        <Typography variant="body1" component="div" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                            {request.typeLabel || request.type} Leave
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.dark', mt: 0.5 }}>
                                            <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography variant="body2" component="div" sx={{ color: 'success.dark' }}>
                                                {formatDate(request.startDate)} to {formatDate(request.endDate)}
                                                {request.duration && ` (${request.duration} day${request.duration !== 1 ? 's' : ''})`}
                                            </Typography>
                                        </Box>
                                        {request.reason && (
                                            <Typography variant="body2" component="div" sx={{ mt: 0.5, color: 'success.dark' }}>
                                                Reason: {request.reason}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    )}

                    {approvedRequests.some(req => req.status === "upcoming") && (
                        <Box>
                            <Typography variant="subtitle1" component="h3" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Upcoming Approved Time Off
                            </Typography>

                            <List dense disablePadding>
                                {approvedRequests
                                    .filter(req => req.status === "upcoming")
                                    .map(request => (
                                        <ListItem
                                            key={request.id}
                                            sx={{
                                                mb: 1,
                                                bgcolor: 'background.paper',
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <CustomListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box component="div">
                                                            {request.typeLabel || request.type} Leave
                                                        </Box>
                                                        <Chip
                                                            label="Approved"
                                                            size="small"
                                                            color="success"
                                                            sx={{ height: 20 }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                        <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                        <Box component="span">
                                                            {formatDate(request.startDate)} to {formatDate(request.endDate)}
                                                            {request.duration && (
                                                                <Box component="span" sx={{ ml: 0.5 }}>
                                                                    ({request.duration} day{request.duration !== 1 ? 's' : ''})
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                            </List>
                        </Box>
                    )}
                </>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="text"
                    color="primary"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => window.location.href = "/timeoff"}
                >
                    View All Time Off
                </Button>
            </Box>
        </Paper>
    );
};

export default EmployeeTimeOffSummary;